import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, Reorder, motion, useDragControls } from "framer-motion";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import PostAddOutlinedIcon from "@mui/icons-material/PostAddOutlined";
import StarOutlineOutlinedIcon from "@mui/icons-material/StarOutlineOutlined";
import Button from "./UiElements/Button";
import Input from "./UiElements/Input";
import Switch from "./UiElements/Switch";
import SelectBox from "./SelectBox";
import Menu3DotsButton from "./icons/Menu3DotsButton";
import { tokens } from "../design-system/tokens";
import listStyles from "../pages/TestDriveList.module.css";
import styles from "./TestDriveFeedbackEditor.module.css";

export type FormOption = {
  value: string;
  label: string;
  allowsFreeText?: boolean;
};

export type FormQuestion = {
  key: string;
  label: string;
  type: "singleSelect" | "text" | "starRating";
  required: boolean;
  options?: FormOption[];
};

export const STAR_RATING_OPTIONS: FormOption[] = [
  { value: "1", label: "⭐" },
  { value: "2", label: "⭐⭐" },
  { value: "3", label: "⭐⭐⭐" },
  { value: "4", label: "⭐⭐⭐⭐" },
  { value: "5", label: "⭐⭐⭐⭐⭐" },
];

export type ExperienceForm = {
  _id: string;
  version: number;
  name?: string;
  isDefault: boolean;
  questions: FormQuestion[];
};

export function getFeedbackFormDisplayName(
  form: Pick<ExperienceForm, "version" | "name">
): string {
  const trimmed = form.name?.trim();
  return trimmed || `גרסה ${form.version}`;
}

export type TestDriveFeedbackEditorProps = {
  formsError: string;
  formsLoading: boolean;
  forms: ExperienceForm[];
  selectedVersion: number | null;
  selectedForm: ExperienceForm | null;
  editorQuestions: FormQuestion[];
  editorFormName: string;
  saving: boolean;
  tableCellStyle: React.CSSProperties;
  onSelectVersion: (version: number) => void;
  onUpdateFormName: (name: string) => void;
  onCreateNewVersion: () => void;
  onDuplicateCurrentVersion: () => void;
  onAddQuestion: () => void;
  onSetDefaultVersion: () => void;
  onOpenDeleteFormVersionModal: () => void;
  onSaveVersion: () => void;
  onUpdateQuestion: (index: number, patch: Partial<FormQuestion>) => void;
  onDeleteQuestion: (index: number) => void;
  onReorderQuestions: (questions: FormQuestion[]) => void;
  onUpdateOption: (
    questionIndex: number,
    optionIndex: number,
    patch: Partial<FormOption>
  ) => void;
  onDeleteOption: (questionIndex: number, optionIndex: number) => void;
  onAddOption: (questionIndex: number) => void;
};

const VIEWPORT_MARGIN = 8;
const ANCHOR_GAP = 8;
const MENU_WIDTH = 220;

const menuPanelStyle: React.CSSProperties = {
  backgroundColor: tokens.colors.background,
  boxShadow: tokens.shadows.md,
  borderRadius: tokens.borderRadius.xl,
  ["--td-actions-menu-item-hover-bg" as string]: tokens.buttonColors.secondaryLightHover,
};

const menuItemStyle: React.CSSProperties = {
  ...tokens.typography.menuItem,
  color: tokens.colors.text.primary,
  borderRadius: tokens.borderRadius.md,
};

const menuIconSx = { fontSize: 20, flexShrink: 0 } as const;

type FeedbackToolbarMenuItem = {
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  onClick: () => void;
};

type FeedbackQuestionCardProps = {
  question: FormQuestion;
  questionIndex: number;
  tableCellStyle: React.CSSProperties;
  onUpdateQuestion: (index: number, patch: Partial<FormQuestion>) => void;
  onDeleteQuestion: (index: number) => void;
  onUpdateOption: (
    questionIndex: number,
    optionIndex: number,
    patch: Partial<FormOption>
  ) => void;
  onDeleteOption: (questionIndex: number, optionIndex: number) => void;
  onAddOption: (questionIndex: number) => void;
};

const FeedbackQuestionCard: React.FC<FeedbackQuestionCardProps> = ({
  question,
  questionIndex,
  tableCellStyle,
  onUpdateQuestion,
  onDeleteQuestion,
  onUpdateOption,
  onDeleteOption,
  onAddOption,
}) => {
  const dragControls = useDragControls();

  return (
    <Reorder.Item
      as="div"
      value={question}
      dragListener={false}
      dragControls={dragControls}
      className={`${listStyles.feedbackBlock} ${styles.reorderItem}`}
      whileDrag={{
        scale: 1.01,
        boxShadow: tokens.shadows.md,
        zIndex: 5,
      }}
    >
      <div className={listStyles.editorQuestionHeader}>
        <div className={styles.questionHeaderStart}>
          <button
            type="button"
            className={`ui-elements-button ${styles.dragHandle}`}
            aria-label="גרור לשינוי סדר השאלה"
            onPointerDown={(event) => dragControls.start(event)}
          >
            <DragIndicatorIcon sx={{ fontSize: 22 }} aria-hidden />
          </button>
          <p style={tableCellStyle}>
            <strong>שאלה {questionIndex + 1}</strong>
          </p>
        </div>
        <Button
          label="מחק שאלה"
          color="outlineAccent"
          size="small"
          width={95}
          onClick={() => onDeleteQuestion(questionIndex)}
        />
      </div>

      <div className={styles.questionBody}>
        <div className={styles.questionFields}>
          <Input
            label="כותרת שאלה"
            value={question.label}
            onChange={(e) => onUpdateQuestion(questionIndex, { label: e.target.value })}
            color="outline"
            size="small"
            rtl
            maxWidth={450}
          />
        </div>

        <div className={styles.questionToolbar}>
          <SelectBox
            label="סוג שאלה"
            name={`question-type-${questionIndex}`}
            value={question.type}
            onChange={(event) => {
              const nextType = event.target.value as FormQuestion["type"];
              onUpdateQuestion(questionIndex, {
                type: nextType,
                options:
                  nextType === "singleSelect"
                    ? question.options && question.options.length > 0
                      ? question.options
                      : [
                          {
                            value: "option_1",
                            label: "אפשרות 1",
                            allowsFreeText: false,
                          },
                        ]
                    : [],
              });
            }}
            options={[
              { value: "singleSelect", label: "בחירה" },
              { value: "text", label: "טקסט חופשי" },
              { value: "starRating", label: "דירוג כוכבים" },
            ]}
            color="outline"
            size="small"
            width={140}
            rtl
          />
          <Switch
            name={`question-required-${questionIndex}`}
            label="חובה"
            checked={question.required}
            onChange={(required) => onUpdateQuestion(questionIndex, { required })}
            size="small"
          />
        </div>

        {question.type === "starRating" ? (
          <div className={styles.optionsList}>
            <span style={{ ...tokens.typography.bodySmall, color: tokens.colors.text.secondary }}>
              דירוג קבוע 1–5 (תוויות כוכבים בלבד):
            </span>
            <div className={styles.optionRow} style={{ flexWrap: "wrap", gap: 8 }}>
              {STAR_RATING_OPTIONS.map((option) => (
                <span key={option.value} style={{ fontSize: 18 }}>
                  {option.label}
                </span>
              ))}
            </div>
          </div>
        ) : null}

        {question.type === "singleSelect" ? (
          <div className={styles.optionsList}>
            {(question.options || []).map((option, optionIndex) => (
              <div key={`${option.value}-${optionIndex}`} className={styles.optionRow}>
                <Input
                  label="טקסט מוצג"
                  value={option.label}
                  onChange={(e) =>
                    onUpdateOption(questionIndex, optionIndex, {
                      label: e.target.value,
                    })
                  }
                  color="outline"
                  size="small"
                  width={300}
                  rtl
                />
                <Switch
                  name={`option-detail-${questionIndex}-${optionIndex}`}
                  label="פרט אחרי בחירה"
                  checked={!!option.allowsFreeText}
                  onChange={(allowsFreeText) =>
                    onUpdateOption(questionIndex, optionIndex, { allowsFreeText })
                  }
                  size="small"
                />
                <Button
                  label="מחק"
                  color="outlineAccent"
                  size="small"
                  width={65}
                  onClick={() => onDeleteOption(questionIndex, optionIndex)}
                />
              </div>
            ))}
            <Button
              label="הוסף אפשרות"
              color="secondaryLight"
              size="small"
              width={110}
              onClick={() => onAddOption(questionIndex)}
            />
          </div>
        ) : null}
      </div>
    </Reorder.Item>
  );
};

const TestDriveFeedbackEditor: React.FC<TestDriveFeedbackEditorProps> = ({
  formsError,
  formsLoading,
  forms,
  selectedVersion,
  selectedForm,
  editorQuestions,
  editorFormName,
  saving,
  tableCellStyle,
  onSelectVersion,
  onUpdateFormName,
  onCreateNewVersion,
  onDuplicateCurrentVersion,
  onAddQuestion,
  onSetDefaultVersion,
  onOpenDeleteFormVersionModal,
  onSaveVersion,
  onUpdateQuestion,
  onDeleteQuestion,
  onReorderQuestions,
  onUpdateOption,
  onDeleteOption,
  onAddOption,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const portalMenuRef = useRef<HTMLDivElement>(null);

  const handleToggleMenu = () => {
    setMenuOpen((prev) => !prev);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  useLayoutEffect(() => {
    if (!menuOpen || !triggerRef.current || !portalMenuRef.current) {
      setMenuPosition(null);
      return;
    }

    const anchor = triggerRef.current.getBoundingClientRect();
    const menu = portalMenuRef.current;
    const menuRect = menu.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let top = anchor.bottom + ANCHOR_GAP;
    if (top + menuRect.height > vh - VIEWPORT_MARGIN) {
      top = anchor.top - menuRect.height - ANCHOR_GAP;
    }
    top = Math.max(VIEWPORT_MARGIN, Math.min(top, vh - menuRect.height - VIEWPORT_MARGIN));

    let left = anchor.right - MENU_WIDTH;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - MENU_WIDTH - VIEWPORT_MARGIN));

    setMenuPosition({ top, left });
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const isInsideTargets = (node: Node | null) => {
      if (!node) return false;
      if (triggerRef.current?.contains(node)) return true;
      if (portalMenuRef.current?.contains(node)) return true;
      return false;
    };

    const handlePointer = (event: MouseEvent | TouchEvent) => {
      const target = (event.target as Node) ?? null;
      if (!isInsideTargets(target)) {
        setMenuOpen(false);
      }
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    const handleScrollOrResize = () => {
      setMenuOpen(false);
    };

    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("touchstart", handlePointer, { passive: true });
    document.addEventListener("keydown", handleKey);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [menuOpen]);

  const getMenuFloatingStyle = (): React.CSSProperties => {
    const anchor = triggerRef.current?.getBoundingClientRect();
    if (!menuOpen || !anchor) {
      return { top: -9999, left: -9999, visibility: "hidden" };
    }

    const vw = window.innerWidth;
    let left = anchor.right - MENU_WIDTH;
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - MENU_WIDTH - VIEWPORT_MARGIN));

    const top = menuPosition?.top ?? anchor.bottom + ANCHOR_GAP;

    return {
      top,
      left,
      width: MENU_WIDTH,
      visibility: menuPosition ? "visible" : "hidden",
      transformOrigin: "top right",
    };
  };

  const floatingStyle = getMenuFloatingStyle();

  const toolbarMenuItems: FeedbackToolbarMenuItem[] = [
    {
      label: "הוסף גרסה חדשה",
      icon: <AddOutlinedIcon sx={menuIconSx} aria-hidden />,
      disabled: saving,
      onClick: onCreateNewVersion,
    },
    {
      label: "שכפל קיים",
      icon: <ContentCopyOutlinedIcon sx={menuIconSx} aria-hidden />,
      disabled: saving || !selectedVersion,
      onClick: onDuplicateCurrentVersion,
    },
    {
      label: "הוסף שאלה",
      icon: <PostAddOutlinedIcon sx={menuIconSx} aria-hidden />,
      disabled: saving || !selectedVersion,
      onClick: onAddQuestion,
    },
    {
      label: "קבע כברירת מחדל",
      icon: <StarOutlineOutlinedIcon sx={menuIconSx} aria-hidden />,
      disabled: saving || !selectedVersion || selectedForm?.isDefault,
      onClick: onSetDefaultVersion,
    },
    {
      label: "מחק גרסה",
      icon: <DeleteOutlineOutlinedIcon sx={menuIconSx} aria-hidden />,
      disabled: saving || !selectedVersion || forms.length <= 1,
      onClick: onOpenDeleteFormVersionModal,
    },
  ];

  return (
    <div className={listStyles.feedbackEditor}>
      {formsError ? <p className={listStyles.error}>{formsError}</p> : null}
      {formsLoading ? (
        <p style={tableCellStyle}>טוען גרסאות...</p>
      ) : (
        <>
          <div className={listStyles.versionTabs}>
            {forms.map((form) => (
              <Button
                key={form.version}
                label={`${getFeedbackFormDisplayName(form)}${form.isDefault ? " (ברירת מחדל)" : ""}`}
                color={selectedVersion === form.version ? "secondary" : "outlineSecondary"}
                size="medium"
                width={"auto"}
                onClick={() => onSelectVersion(form.version)}
              />
            ))}
          </div>

          {selectedVersion ? (
            <div className={`${listStyles.fieldGroup} ${styles.versionNameField}`}>
              <Input
                value={editorFormName}
                onChange={(e) => onUpdateFormName(e.target.value)}
                color="outline"
                size="large"
                rtl
                maxWidth={300}
                label="שם גרסה"
                placeholder={`גרסה ${selectedVersion}`}
              />
            </div>
          ) : null}

          <div className={styles.toolbar}>
            <Button
              label="שמור שינויים"
              color="primary"
              size="small"
              width={"auto"}
              onClick={onSaveVersion}
              disabled={saving || !selectedVersion}
            />
            <Menu3DotsButton
              ref={triggerRef}
              className={styles.menuTrigger}
              color="primary"
              buttonSize="small"
              aria-label="פעולות עורך משוב"
              aria-expanded={menuOpen}
              onClick={handleToggleMenu}
            />
            {createPortal(
              <AnimatePresence>
                {menuOpen ? (
                  <motion.div
                    ref={portalMenuRef}
                    className={styles.menuContent}
                    style={{ ...menuPanelStyle, ...floatingStyle }}
                    initial={{ opacity: 0, scaleY: 0 }}
                    animate={
                      menuPosition
                        ? { opacity: 1, scaleY: 1 }
                        : { opacity: 0, scaleY: 0 }
                    }
                    exit={{ opacity: 0, scaleY: 0 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                  >
                    {toolbarMenuItems.map((item) => (
                      <motion.div
                        key={item.label}
                        className={`${styles.menuItem}${item.disabled ? ` ${styles.menuItemDisabled}` : ""}`}
                        onClick={() => {
                          if (item.disabled) return;
                          closeMenu();
                          item.onClick();
                        }}
                        style={{ ...menuItemStyle, userSelect: "none" }}
                      >
                        <span className={styles.menuItemRow}>
                          {item.icon}
                          <span>{item.label}</span>
                        </span>
                      </motion.div>
                    ))}
                  </motion.div>
                ) : null}
              </AnimatePresence>,
              document.body
            )}
          </div>

          {selectedVersion && editorQuestions.length === 0 ? (
            <p style={tableCellStyle}>אין שאלות בגרסה זו</p>
          ) : null}

          {editorQuestions.length > 0 ? (
            <Reorder.Group
              as="div"
              axis="y"
              values={editorQuestions}
              onReorder={onReorderQuestions}
              className={styles.questionsList}
            >
              {editorQuestions.map((question, questionIndex) => (
                <FeedbackQuestionCard
                  key={question.key}
                  question={question}
                  questionIndex={questionIndex}
                  tableCellStyle={tableCellStyle}
                  onUpdateQuestion={onUpdateQuestion}
                  onDeleteQuestion={onDeleteQuestion}
                  onUpdateOption={onUpdateOption}
                  onDeleteOption={onDeleteOption}
                  onAddOption={onAddOption}
                />
              ))}
            </Reorder.Group>
          ) : null}

          {selectedVersion ? (
            <div className={`${styles.toolbar} ${styles.footerToolbar}`}>
              <Button
                label="שמור שינויים"
                color="primary"
                size="small"
                width={"auto"}
                onClick={onSaveVersion}
                disabled={saving || !selectedVersion}
              />
              <Button
                label="הוסף שאלה"
                color="secondaryLight"
                size="small"
                width={"auto"}
                onClick={onAddQuestion}
                disabled={saving || !selectedVersion}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default TestDriveFeedbackEditor;
